/**
 * Crypt Dice SVG Data
 *
 * SVG paths, pip geometry, and layout data for the crypt dice design.
 * Paths extracted from Figma exports (red_dice_3 and yellow_dice_6 reference SVGs).
 * The crypt dice features an ornate gold/bronze frame with white bone/candle
 * decorations scattered around the border, and a green grass inner face.
 * Pip indicators use bone/candle shapes at standard dice face positions.
 */

// ─── Shared Frame Colors ──────────────────────────────────────────────

export const CRYPT_FRAME_COLORS = {
  CORNER_FILL: '#72531C',
  FRAME_GOLD_START: '#876C3D',
  FRAME_GOLD_END: '#947636',
  FRAME_DARK_START: '#4C4018',
  FRAME_DARK_END: '#443427'
} as const;

// ─── SVG Paths (from reference SVGs) ─────────────────────────────────

export const CRYPT_PATHS = {
  // Bottom shadow layer (opacity 0.12)
  SHADOW_BOTTOM_A:
    'M15.97 164.3V178.08C15.97 182.11 18.04 185.88 21.44 187.78C21.47 187.8 21.49 187.81 21.52 187.83C25.47 190.01 29.19 189.51 33.69 189.29C39.18 189.02 44.66 189.87 50.16 189.86C61.1 189.86 72.05 189.88 82.99 189.86C90.86 189.85 98.61 189.86 104.15 189.86C112.64 189.86 116.88 189.87 117.32 189.88C135.97 190.18 137.45 191.5 144.06 189.88C148.63 188.76 150.27 187.56 154.54 187.62C158.58 187.68 161.18 188.82 164.52 187.41C167.25 186.26 169.21 183.56 169.21 183.56C169.21 183.56 169.22 183.55 169.23 183.54C170.02 182.58 170.44 181.34 170.43 180.07C170.37 174.09 170.32 168.1 170.26 162.11C145.6 165.97 116.97 168.74 85.1 168.72C59.76 168.71 36.58 166.93 15.96 164.3H15.97Z',
  SHADOW_BOTTOM_B:
    'M15.97 154.58V172.76C17.03 174.4 18.79 176.59 21.53 178.1C25.48 180.28 29.2 179.78 33.7 179.56C39.19 179.29 44.67 180.14 50.17 180.13C61.11 180.13 72.06 180.15 83 180.13C90.87 180.12 98.62 180.13 104.16 180.13C112.65 180.13 116.89 180.14 117.33 180.15C135.98 180.45 137.46 181.77 144.07 180.15C148.64 179.03 150.28 177.83 154.55 177.89C158.59 177.95 161.19 179.09 164.53 177.68C167.26 176.53 169.22 173.83 169.22 173.83C169.77 173.17 170.18 172.57 170.45 172.13C170.39 165.55 170.33 158.97 170.27 152.39C145.61 156.25 116.98 159.02 85.11 159C59.77 158.99 36.59 157.21 15.97 154.58Z',
  SHADOW_OUTER_ROUND:
    'M133.56 18.7101H52.86C32.4862 18.7101 15.97 35.2263 15.97 55.6001V135.88C15.97 156.254 32.4862 172.77 52.86 172.77H133.56C153.934 172.77 170.45 156.254 170.45 135.88V55.6001C170.45 35.2263 153.934 18.7101 133.56 18.7101Z',
  SHADOW_INNER_ROUND_A:
    'M120.09 32.1501H66.34C45.9662 32.1501 29.45 48.6664 29.45 69.0401V122.44C29.45 142.814 45.9662 159.33 66.34 159.33H120.09C140.464 159.33 156.98 142.814 156.98 122.44V69.0401C156.98 48.6664 140.464 32.1501 120.09 32.1501Z',
  SHADOW_INNER_ROUND_B:
    'M126.27 35.3601H60.16C44.9777 35.3601 32.67 47.6678 32.67 62.8501V128.63C32.67 143.812 44.9777 156.12 60.16 156.12H126.27C141.452 156.12 153.76 143.812 153.76 128.63V62.8501C153.76 47.6678 141.452 35.3601 126.27 35.3601Z',

  // Ground/ledge bottom layers
  LEDGE_BOTTOM:
    'M170.43 168.03C170.44 169.3 170.03 170.54 169.23 171.5C169.23 171.5 169.22 171.52 169.21 171.52C169.21 171.52 167.25 174.22 164.52 175.37C161.18 176.77 158.59 175.64 154.54 175.58C150.27 175.52 148.64 176.72 144.06 177.84C137.45 179.46 135.96 178.14 117.32 177.84C116.88 177.84 112.63 177.83 104.15 177.82C98.61 177.82 90.86 177.81 82.99 177.82C72.05 177.84 61.1 177.82 50.16 177.82C44.67 177.82 39.18 176.98 33.69 177.25C29.19 177.47 25.46 177.97 21.52 175.79C21.49 175.77 21.47 175.76 21.44 175.74C18.04 173.83 15.97 170.07 15.97 166.04V152.26C36.59 154.9 59.77 156.67 85.11 156.69C92.56 156.69 99.84 156.55 106.93 156.26C130.16 155.35 151.38 153.03 170.27 150.07C170.3 153.46 170.33 156.85 170.36 160.24C170.36 160.4 170.36 160.56 170.36 160.72C170.37 162.1 170.38 163.49 170.4 164.87C170.4 165.92 170.42 166.97 170.43 168.02V168.03Z',
  LEDGE_TOP:
    'M15.97 144.03V162.21C17.03 163.85 18.79 166.04 21.53 167.55C25.48 169.73 29.2 169.23 33.7 169.01C39.19 168.74 44.67 169.59 50.17 169.58C61.11 169.58 72.06 169.6 83 169.58C90.87 169.57 98.62 169.58 104.16 169.58C112.65 169.58 116.89 169.59 117.33 169.6C135.98 169.9 137.46 171.22 144.07 169.6C148.64 168.48 150.28 167.28 154.55 167.34C158.59 167.4 161.19 168.54 164.53 167.13C167.26 165.98 169.22 163.28 169.22 163.28C169.77 162.62 170.18 162.02 170.45 161.58C170.39 155 170.33 148.42 170.27 141.84C145.61 145.7 116.98 148.47 85.11 148.45C59.77 148.44 36.59 146.66 15.97 144.03Z',

  // Main body rectangle
  BODY_RECT: 'M170.45 6.67004H15.97V160.73H170.45V6.67004Z',

  // Corner/edge decorations (player-colored)
  CORNER_LEFT:
    'M33.23 124.38C30.26 128.23 25.21 133.83 15.97 143.89V114.22C25.11 102.61 27.39 101.83 29.44 97.2201C35.13 84.4001 25.29 72.9101 29.46 69.8001C33.98 66.4301 51.03 75.8101 53.25 88.8401C54.59 96.6701 47.47 105.9 33.23 124.37V124.38Z',
  CORNER_RIGHT:
    'M170.45 109.4V160.72H109.79C108.63 159.21 107.66 157.7 106.93 156.25C105.36 153.16 104.85 150.33 105.7 148.33C106.01 147.61 106.48 147.02 107.09 146.53C111.67 142.87 124.19 144.85 135.31 144.68C138.63 144.63 141.83 144.39 144.65 143.74C148.8 142.8 152.14 140.99 153.88 137.67C158.32 129.2 148.74 117.77 154.58 111.53C157.48 108.43 162.2 108.71 170.44 109.39L170.45 109.4Z',
  CORNER_TOP_LEFT:
    'M87.69 27.09C86.89 28.11 85.84 28.74 84.59 29.08C82.78 29.59 80.56 29.49 78.05 29.08C67.91 27.45 53.22 20.82 43.21 29C36.96 34.11 39.55 39.27 31.43 47.08C27.54 50.82 21.81 54.59 16.17 56.41C16.1 56.43 16.04 56.45 15.97 56.47V26.85C16.04 26.83 16.1 26.82 16.17 26.8C31.91 23.32 36.48 23.1 37.63 20.05C39.29 15.67 31.48 11.77 31.36 6.65002H84.19C89 14.05 90.96 22.95 87.69 27.07V27.09Z',

  // Right frame (gold)
  FRAME_RIGHT_OUTER:
    'M170.7 6.87V160.22C168.56 158.89 166.43 158.06 164.3 156.72V146.22C163.86 144.63 162.97 142.15 161.12 139.6C159.21 136.96 157.08 135.37 155.71 134.5C156.93 133.78 158.5 132.67 160 130.99C162.73 127.95 163.83 124.66 164.29 122.84V96.68C163.81 95.12 162.95 92.92 161.35 90.61C159.59 88.08 157.65 86.44 156.3 85.47C157.53 84.59 159.14 83.23 160.63 81.21C162.97 78.03 163.83 74.82 164.19 72.97C164.35 64.67 164.3 57.72 164.19 52.41C164.15 50.5 164.08 48.05 162.92 45.22C161.13 40.86 157.86 38.25 155.71 36.86C157.31 35.55 161.87 31.45 163.56 24.32C164.69 19.58 164.05 15.57 163.56 13.45C165.94 11.26 168.32 9.06 170.69 6.87H170.7Z',
  FRAME_RIGHT_INNER:
    'M170.7 9.44005V158.97C169.5 158.22 168.29 157.46 167.09 156.71V146.21C166.65 144.62 165.76 142.14 163.91 139.59C161.99 136.95 159.87 135.36 158.5 134.49C159.72 133.77 161.29 132.66 162.79 130.98C165.52 127.94 166.62 124.65 167.08 122.83V96.6701C166.6 95.1101 165.74 92.91 164.14 90.6001C162.38 88.0701 160.44 86.4301 159.09 85.4601C160.32 84.5801 161.93 83.22 163.42 81.2001C165.76 78.0201 166.63 74.8101 166.98 72.9601C167.14 64.6601 167.09 57.7101 166.98 52.4001C166.94 50.4901 166.87 48.0401 165.71 45.2101C163.92 40.8501 160.65 38.2401 158.5 36.8501C160.1 35.5401 164.66 31.4401 166.35 24.3101C167.48 19.5701 166.84 15.5601 166.35 13.4401C167.8 12.1101 169.25 10.7701 170.69 9.43005L170.7 9.44005Z',

  // Left frame (gold)
  FRAME_LEFT_OUTER:
    'M15.97 160.72V6.87C18.11 8.2 20.24 9.54 22.37 10.88V21.38C22.81 22.97 23.7 25.45 25.55 28C27.46 30.64 29.59 32.23 30.96 33.1C29.74 33.82 28.17 34.93 26.67 36.61C23.94 39.65 22.84 42.94 22.38 44.76V70.92C22.86 72.48 23.72 74.68 25.32 76.99C27.08 79.52 29.02 81.16 30.37 82.13C29.14 83.01 27.53 84.37 26.04 86.39C23.7 89.57 22.84 92.78 22.48 94.63C22.32 102.93 22.37 109.88 22.48 115.19C22.52 117.1 22.59 119.55 23.75 122.38C25.54 126.74 28.81 129.35 30.96 130.74C29.36 132.05 24.8 136.15 23.11 143.28C21.98 148.02 22.62 152.03 23.11 154.15C20.73 156.34 18.35 158.54 15.98 160.73L15.97 160.72Z',
  FRAME_LEFT_INNER:
    'M15.97 158.15V8.62C17.17 9.37 18.38 10.13 19.58 10.88V21.38C20.02 22.97 20.91 25.45 22.76 28C24.68 30.64 26.8 32.23 28.17 33.1C26.95 33.82 25.38 34.93 23.88 36.61C21.15 39.65 20.05 42.94 19.59 44.76V70.92C20.07 72.48 20.93 74.68 22.53 76.99C24.29 79.52 26.23 81.16 27.58 82.13C26.35 83.01 24.74 84.37 23.25 86.39C20.91 89.57 20.04 92.78 19.69 94.63C19.53 102.93 19.58 109.88 19.69 115.19C19.73 117.1 19.8 119.55 20.96 122.38C22.75 126.74 26.02 129.35 28.17 130.74C26.57 132.05 22.01 136.15 20.32 143.28C19.19 148.02 19.83 152.03 20.32 154.15C18.87 155.48 17.42 156.82 15.98 158.16L15.97 158.15Z',

  // Top frame (gold)
  FRAME_TOP_OUTER:
    'M19.45 6.67004H166.95C165.67 8.90004 164.39 11.12 163.11 13.35H153.05C151.53 13.81 149.15 14.73 146.7 16.67C144.17 18.66 142.65 20.88 141.81 22.31C141.12 21.03 140.06 19.4 138.45 17.83C135.53 14.98 132.38 13.83 130.64 13.35H105.56C104.07 13.85 101.95 14.75 99.74 16.41C97.31 18.24 95.74 20.27 94.81 21.68C93.97 20.4 92.66 18.72 90.72 17.16C87.67 14.72 84.59 13.82 82.82 13.45C74.86 13.28 68.2 13.34 63.11 13.45C61.28 13.49 58.93 13.57 56.22 14.78C52.04 16.64 49.54 20.06 48.21 22.3C46.95 20.63 43.02 15.88 36.19 14.11C31.64 12.93 27.8 13.59 25.77 14.11C23.67 11.63 21.56 9.15004 19.46 6.67004H19.45Z',
  FRAME_TOP_INNER:
    'M21.92 6.67004H165.28C164.56 7.93004 163.84 9.18004 163.11 10.44H153.05C151.53 10.9 149.15 11.82 146.7 13.75C144.17 15.75 142.65 17.96 141.81 19.4C141.12 18.12 140.06 16.49 138.45 14.92C135.53 12.07 132.38 10.92 130.64 10.44H105.56C104.07 10.94 101.95 11.84 99.74 13.5C97.31 15.33 95.74 17.36 94.81 18.77C93.97 17.49 92.66 15.81 90.72 14.25C87.67 11.81 84.59 10.91 82.82 10.54C74.86 10.37 68.2 10.43 63.11 10.54C61.28 10.58 58.93 10.65 56.22 11.87C52.04 13.73 49.54 17.15 48.21 19.39C46.95 17.72 43.02 12.97 36.19 11.2C31.64 10.02 27.8 10.68 25.77 11.2C24.49 9.69004 23.21 8.18004 21.93 6.67004H21.92Z',

  // Bottom frame (gold)
  FRAME_BOTTOM_OUTER:
    'M167.21 160.68H19.71C20.99 158.45 22.27 156.23 23.55 154H33.61C35.13 153.54 37.51 152.62 39.96 150.68C42.49 148.69 44.01 146.47 44.85 145.04C45.54 146.32 46.6 147.95 48.21 149.52C51.13 152.37 54.28 153.52 56.02 154H81.1C82.59 153.5 84.71 152.6 86.92 150.94C89.35 149.11 90.92 147.08 91.85 145.67C92.69 146.95 94 148.63 95.94 150.19C98.99 152.63 102.07 153.53 103.84 153.9C111.8 154.07 118.46 154.01 123.55 153.9C125.38 153.86 127.73 153.78 130.44 152.57C134.62 150.71 137.12 147.29 138.45 145.05C139.71 146.72 143.64 151.47 150.47 153.24C155.02 154.42 158.86 153.76 160.89 153.24C162.99 155.72 165.1 158.2 167.2 160.68H167.21Z',
  FRAME_BOTTOM_INNER:
    'M164.74 160.68H21.38C22.1 159.42 22.82 158.17 23.55 156.91H33.61C35.13 156.45 37.51 155.53 39.96 153.6C42.49 151.6 44.01 149.39 44.85 147.95C45.54 149.23 46.6 150.86 48.21 152.43C51.13 155.28 54.28 156.43 56.02 156.91H81.1C82.59 156.41 84.71 155.51 86.92 153.85C89.35 152.02 90.92 149.99 91.85 148.58C92.69 149.86 94 151.54 95.94 153.1C98.99 155.54 102.07 156.44 103.84 156.81C111.8 156.98 118.46 156.92 123.55 156.81C125.38 156.77 127.73 156.7 130.44 155.48C134.62 153.62 137.12 150.2 138.45 147.96C139.71 149.63 143.64 154.38 150.47 156.15C155.02 157.33 158.86 156.67 160.89 156.15C162.17 157.66 163.45 159.17 164.73 160.68H164.74Z',

  // Corner triangles
  CORNER_TR_FILL:
    'M170.7 6.67004V15.24C170.63 15.28 170.55 15.32 170.47 15.37C167.2 17.17 157.22 22.3 157.22 22.3L160.95 6.67004H170.7Z',
  CORNER_TR_GRAD: 'M157.23 22.3L170.7 6.67004H160.96L157.23 22.3Z',
  CORNER_TL_FILL:
    'M15.97 6.67004H24.19C24.23 6.74004 24.27 6.82004 24.32 6.91004C26.04 10.32 30.96 20.73 30.96 20.73L15.97 16.84V6.67004Z',
  CORNER_TL_GRAD: 'M30.96 20.72L15.97 6.67004V16.83L30.96 20.72Z',
  CORNER_BL_FILL:
    'M15.9901 160.71V152.14C16.0601 152.1 16.1401 152.06 16.2201 152.01C19.4901 150.21 29.4701 145.08 29.4701 145.08L25.7401 160.71H15.9901Z',
  CORNER_BL_GRAD: 'M29.4601 145.08L15.9901 160.71H25.7301L29.4601 145.08Z',
  CORNER_BR_FILL:
    'M170.7 160.72H162.48C162.44 160.65 162.4 160.57 162.35 160.48C160.63 157.07 155.71 146.66 155.71 146.66L170.7 150.55V160.71V160.72Z',
  CORNER_BR_GRAD: 'M155.71 146.67L170.7 160.72V150.56L155.71 146.67Z',

  // Inner grass face
  INNER_FACE:
    'M140.2 21.5801H46.99C38.5842 21.5801 31.77 28.3943 31.77 36.8001V130.01C31.77 138.416 38.5842 145.23 46.99 145.23H140.2C148.606 145.23 155.42 138.416 155.42 130.01V36.8001C155.42 28.3943 148.606 21.5801 140.2 21.5801Z',
  INNER_FACE_VERT_OVERLAY:
    'M31.77 37.1401L31.77 129.57C31.77 138.191 38.7589 145.18 47.38 145.18H139.81C148.431 145.18 155.42 138.191 155.42 129.57V37.1401C155.42 28.5189 148.431 21.5301 139.81 21.5301H47.38C38.7589 21.5301 31.77 28.5189 31.77 37.1401Z',
  INNER_FACE_HORIZ_OVERLAY:
    'M140.28 21.5801H46.91C38.5484 21.5801 31.77 28.3585 31.77 36.7201V130.09C31.77 138.452 38.5484 145.23 46.91 145.23H140.28C148.642 145.23 155.42 138.452 155.42 130.09V36.7201C155.42 28.3585 148.642 21.5801 140.28 21.5801Z'
} as const;

// ─── White Bone/Candle Decorations (opacity 0.4) ──────────────────────
// These scattered white shapes decorate the border area of the crypt dice

export const CRYPT_DECORATIONS = [
  // Top-right candle decoration
  'M155.62 25.1C153.93 22.44 149.59 22.02 147.66 24.36C146.1 26.24 146.35 29.62 147.68 30.73C148.11 31.09 148.72 31.91 148.72 31.91C148.72 31.91 148.59 33.45 148.32 34.56C148.55 34.6 148.79 34.64 149.02 34.68L149.28 33.6L149.63 33.42L149.32 34.73C149.58 34.77 149.84 34.82 150.11 34.86L150.41 33.59L150.78 33.4L150.42 34.91C150.7 34.96 150.98 35 151.26 35.05L151.58 33.72L151.93 33.54L151.56 35.1C151.89 35.16 152.21 35.21 152.54 35.26C152.62 34.44 152.7 33.63 152.78 32.81C153.16 32.82 153.54 32.76 153.9 32.6C155.93 31.71 157.28 27.7 155.63 25.1H155.62ZM151.34 28.77C150.75 29.26 148.64 28.31 148.55 26.96C148.5 26.19 149.09 25.25 149.73 25.23C150.14 25.22 150.45 25.59 150.67 25.86C151.43 26.78 151.85 28.36 151.35 28.78L151.34 28.77ZM155.09 27.8C154.43 28.97 152.14 28.83 151.82 28.12C151.54 27.51 152.6 26.29 153.67 25.82C153.98 25.69 154.42 25.49 154.78 25.69C155.34 26 155.47 27.13 155.1 27.79L155.09 27.8Z',
  // Bottom-left bone decoration
  'M34.42 133.87C31.36 133.96 28.73 137.59 29.61 140.54C30.32 142.92 33.2 144.47 34.81 143.88C35.33 143.69 36.32 143.58 36.32 143.58C36.32 143.58 37.5 144.51 38.27 145.33C38.42 145.14 38.58 144.96 38.73 144.77L37.99 143.97L38.03 143.57L38.93 144.54C39.1 144.33 39.28 144.12 39.45 143.91L38.57 142.97L38.61 142.55L39.65 143.67C39.84 143.45 40.02 143.22 40.21 143L39.29 142.01L39.33 141.61L40.41 142.76C40.63 142.5 40.84 142.24 41.06 141.98C40.44 141.48 39.81 140.98 39.19 140.47C39.4 140.14 39.55 139.77 39.61 139.36C39.95 137.09 37.4 133.78 34.41 133.87H34.42ZM35.15 139.59C35.24 140.37 33.35 141.74 32.2 141.11C31.55 140.75 31.09 139.72 31.41 139.15C31.62 138.78 32.09 138.7 32.41 138.65C33.55 138.46 35.07 138.93 35.14 139.59H35.15ZM36.35 135.76C36.95 136.96 35.63 138.92 34.88 138.83C34.24 138.75 33.8 137.17 33.98 135.98C34.03 135.64 34.11 135.15 34.46 134.93C35.01 134.6 36 135.07 36.34 135.76H36.35Z',
  // Top-left bone/arrow decoration A
  'M55.37 24.08L65.49 18.8C65.49 18.8 66.88 15.5 67.89 17.13C68.9 18.76 67.89 18.71 67.89 18.71C67.89 18.71 69.41 18.89 69.11 19.9C68.81 20.91 66.71 20.38 66.71 20.38L56.04 25.44C56.04 25.44 54.77 27.86 54.35 26.76C53.93 25.66 54.9 25.35 54.9 25.35C54.9 25.35 52.92 25.48 53.3 24.38C53.68 23.28 55.37 24.07 55.37 24.07V24.08Z',
  // Top-left bone/arrow decoration B
  'M63.86 24.42L75.09 25.87C75.09 25.87 78 23.93 77.98 25.87C77.96 27.81 77.14 27.19 77.14 27.19C77.14 27.19 78.31 28.21 77.53 28.89C76.75 29.57 75.27 27.9 75.27 27.9L63.7 25.95C63.7 25.95 61.36 27.23 61.59 26.07C61.82 24.91 62.79 25.21 62.79 25.21C62.79 25.21 61.07 24.17 61.97 23.48C62.87 22.78 63.85 24.42 63.85 24.42H63.86Z',
  // Bottom-right bone/arrow decoration A
  'M109.21 141.94L120.44 143.39C120.44 143.39 123.35 141.45 123.33 143.39C123.31 145.33 122.49 144.71 122.49 144.71C122.49 144.71 123.66 145.73 122.88 146.41C122.1 147.08 120.62 145.42 120.62 145.42L109.05 143.47C109.05 143.47 106.71 144.75 106.94 143.59C107.17 142.43 108.14 142.73 108.14 142.73C108.14 142.73 106.42 141.69 107.32 141C108.22 140.31 109.2 141.94 109.2 141.94H109.21Z',
  // Bottom-right bone/arrow decoration B
  'M131.68 145.78L121.14 150.08C121.14 150.08 119.47 153.23 118.6 151.52C117.73 149.8 118.74 149.94 118.74 149.94C118.74 149.94 117.24 149.62 117.63 148.64C118.02 147.66 120.07 148.39 120.07 148.39L131.13 144.36C131.13 144.36 132.6 142.07 132.92 143.21C133.24 144.34 132.25 144.56 132.25 144.56C132.25 144.56 134.24 144.62 133.76 145.68C133.28 146.74 131.68 145.79 131.68 145.79V145.78Z',
  // Left candle decoration
  'M32.62 102.85L32.56 109.03C32.56 109.03 33.7 110.5 32.73 110.6C31.76 110.7 32.02 110.22 32.02 110.22C32.02 110.22 31.58 110.92 31.2 110.53C30.82 110.14 31.56 109.25 31.56 109.25L31.85 102.85C31.85 102.85 31.07 101.66 31.66 101.71C32.25 101.76 32.16 102.31 32.16 102.31C32.16 102.31 32.58 101.31 32.98 101.76C33.38 102.21 32.62 102.84 32.62 102.84V102.85Z',
  // Left bone decoration A
  'M30.41 113.59L28.15 109.12C28.15 109.12 26.78 108.53 27.44 108.06C28.1 107.59 28.09 108.05 28.09 108.05C28.09 108.05 28.15 107.37 28.57 107.49C28.99 107.61 28.79 108.56 28.79 108.56L30.97 113.27C30.97 113.27 31.97 113.81 31.53 114.01C31.09 114.21 30.95 113.78 30.95 113.78C30.95 113.78 31.02 114.66 30.57 114.5C30.12 114.34 30.42 113.58 30.42 113.58L30.41 113.59Z',
  // Left bone decoration B
  'M26.33 106.96L29.16 102.86C29.16 102.86 29.06 101.32 29.75 101.73C30.44 102.14 30.05 102.33 30.05 102.33C30.05 102.33 30.66 102.08 30.74 102.53C30.82 102.98 29.92 103.21 29.92 103.21L26.84 107.34C26.84 107.34 26.82 108.52 26.45 108.19C26.08 107.86 26.39 107.54 26.39 107.54C26.39 107.54 25.66 108 25.6 107.51C25.54 107.02 26.32 106.96 26.32 106.96H26.33Z',
  // Right candle decoration A
  'M156.22 45.5301L157.74 51.5101C157.74 51.5101 159.21 52.6101 158.31 52.9701C157.4 53.3401 157.53 52.8001 157.53 52.8001C157.53 52.8001 157.28 53.5901 156.82 53.3301C156.36 53.0701 156.84 51.9901 156.84 51.9901L155.49 45.7401C155.49 45.7401 154.44 44.8001 155.02 44.7001C155.61 44.5901 155.65 45.1401 155.65 45.1401C155.65 45.1401 155.8 44.0701 156.3 44.3901C156.8 44.7101 156.23 45.5301 156.23 45.5301H156.22Z',
  // Right candle decoration B
  'M155.24 51.5L155.78 56.52C155.78 56.52 156.85 57.59 156.07 57.77C155.29 57.95 155.46 57.54 155.46 57.54C155.46 57.54 155.17 58.15 154.82 57.88C154.47 57.61 154.99 56.8 154.99 56.8L154.61 51.58C154.61 51.58 153.86 50.69 154.35 50.68C154.84 50.67 154.81 51.12 154.81 51.12C154.81 51.12 155.05 50.27 155.42 50.59C155.79 50.91 155.23 51.5 155.23 51.5H155.24Z',
  // Right candle decoration C
  'M151.2 51.24L152.88 46.5C152.88 46.5 152.39 45.04 153.16 45.24C153.93 45.44 153.6 45.73 153.6 45.73C153.6 45.73 154.12 45.32 154.32 45.73C154.52 46.14 153.7 46.61 153.7 46.61L151.79 51.45C151.79 51.45 152.07 52.59 151.63 52.38C151.19 52.17 151.41 51.77 151.41 51.77C151.41 51.77 150.83 52.42 150.64 51.96C150.45 51.5 151.2 51.23 151.2 51.23V51.24Z'
] as const;

// ─── Gradient Definitions ─────────────────────────────────────────────

export const CRYPT_GRADIENTS = {
  // Dark bottom (grass green shadow)
  LEDGE_BOTTOM: {
    x1: 93.2,
    y1: 164.35,
    x2: 93.2,
    y2: 177.4,
    start: '#2F3806',
    end: '#393D04'
  },
  // Light ledge (grass green)
  LEDGE_TOP: {
    x1: 93.21,
    y1: 173.04,
    x2: 93.21,
    y2: 160.32,
    start: '#95A14F',
    end: '#6E7645'
  },
  // Right frame gold
  FRAME_RIGHT_GOLD: {
    x1: 155.71,
    y1: 83.54,
    x2: 170.7,
    y2: 83.54,
    start: '#876C3D',
    end: '#947636'
  },
  // Right frame dark
  FRAME_RIGHT_DARK: {
    x1: 172.22,
    y1: 84.2101,
    x2: 160.03,
    y2: 84.2101,
    start: '#4C4018',
    end: '#443427'
  },
  // Left frame gold
  FRAME_LEFT_GOLD: {
    x1: 15.97,
    y1: 83.8,
    x2: 30.96,
    y2: 83.8,
    start: '#876C3D',
    end: '#947636'
  },
  // Left frame dark
  FRAME_LEFT_DARK: {
    x1: 25.05,
    y1: 83.38,
    x2: 13.88,
    y2: 83.38,
    start: '#4C4018',
    end: '#443427'
  },
  // Top frame gold
  FRAME_TOP_GOLD: {
    x1: 93.2,
    y1: 10.79,
    x2: 93.2,
    y2: 20.69,
    start: '#876C3D',
    end: '#947636'
  },
  // Top frame dark
  FRAME_TOP_DARK: {
    x1: 93.6,
    y1: 7.66004,
    x2: 93.6,
    y2: 17.29,
    start: '#4C4018',
    end: '#443427'
  },
  // Bottom frame gold
  FRAME_BOTTOM_GOLD: {
    x1: 19.71,
    y1: 152.86,
    x2: 167.21,
    y2: 152.86,
    start: '#876C3D',
    end: '#947636'
  },
  // Bottom frame dark
  FRAME_BOTTOM_DARK: {
    x1: 93.06,
    y1: 152.13,
    x2: 93.06,
    y2: 161.48,
    start: '#4C4018',
    end: '#443427'
  },
  // Corner top-right
  CORNER_TR: {
    x1: 157.23,
    y1: 14.48,
    x2: 0.999965,
    y2: 14.48,
    start: '#876C3D',
    end: '#947636'
  },
  // Corner top-left
  CORNER_TL: {
    x1: 15.97,
    y1: 13.7,
    x2: 30.96,
    y2: 13.7,
    start: '#876C3D',
    end: '#947636'
  },
  // Corner bottom-left
  CORNER_BL: {
    x1: 15.9901,
    y1: 152.89,
    x2: 29.4601,
    y2: 152.89,
    start: '#876C3D',
    end: '#947636'
  },
  // Corner bottom-right
  CORNER_BR: {
    x1: 5.32874e-5,
    y1: 153.7,
    x2: 1.00005,
    y2: 153.7,
    start: '#876C3D',
    end: '#947636'
  },
  // Inner face grass
  INNER_FACE: {
    x1: 31.77,
    y1: 83.4101,
    x2: 155.42,
    y2: 83.4101,
    start: '#95A14F',
    end: '#6E7645'
  },
  // Inner face vertical vignette
  INNER_FACE_VERT: {
    x1: 29.06,
    y1: 83.3501,
    x2: 158.37,
    y2: 83.3501
  },
  // Inner face horizontal vignette
  INNER_FACE_HORIZ: {
    x1: 93.6,
    y1: 18.8801,
    x2: 93.6,
    y2: 148.19
  }
} as const;

// ─── Texture Rect Position ────────────────────────────────────────────

export const CRYPT_TEXTURE_RECT = { x: -20.4, y: -20.0, width: 228, height: 228 } as const;

// ─── Dice Face Indicators (exact paths per value) ─────────────────────
// Each dice face value has its own set of indicator shapes.
// Paths are extracted exactly from the reference SVG files (dice_1.svg through dice_6.svg).
// Each indicator consists of a shadow path (fill="#3A3A3A") and a main white path (fill="white").

export interface DiceFaceIndicator {
  shadow: string;
  main: string;
}

// ─── Value 1: single centered diagonal bone ──────────────────────────

const FACE_1: DiceFaceIndicator[] = [
  {
    shadow:
      'M75.85 61.51L113.66 100.91C113.66 100.91 130.23 102.8 124.49 109.69C118.75 116.59 117.49 111.86 117.49 111.86C117.49 111.86 118.89 119.1 113.99 119.13C109.09 119.16 108.44 108.71 108.44 108.71L70.79 66.49C70.79 66.49 58.29 63.98 62.54 60.53C66.8 57.08 69.54 61.12 69.54 61.12C69.54 61.12 66.12 52.17 71.52 52.41C76.92 52.65 75.84 61.51 75.84 61.51H75.85Z',
    main: 'M75.85 62.85L113.66 102.25C113.66 102.25 130.23 104.14 124.49 111.03C118.75 117.92 117.49 113.2 117.49 113.2C117.49 113.2 118.89 120.44 113.99 120.47C109.1 120.5 108.44 110.05 108.44 110.05L70.79 67.83C70.79 67.83 58.29 65.32 62.54 61.87C66.8 58.42 69.54 62.46 69.54 62.46C69.54 62.46 66.12 53.51 71.52 53.75C76.92 53.99 75.84 62.85 75.84 62.85H75.85Z'
  }
];

// ─── Value 2: two vertical bones (left and right columns) ─────────────

const FACE_2: DiceFaceIndicator[] = [
  {
    shadow:
      'M84.69 52.9801V107.59C84.69 107.59 95.33 120.43 86.41 121.42C77.49 122.42 79.86 118.14 79.86 118.14C79.86 118.14 75.85 124.33 72.31 120.96C68.77 117.59 75.52 109.6 75.52 109.6L77.6 53.0701C77.6 53.0701 70.32 42.6001 75.78 43.0601C81.24 43.5201 80.42 48.3401 80.42 48.3401C80.42 48.3401 84.15 39.5101 87.88 43.4301C91.61 47.3501 84.69 52.9901 84.69 52.9901V52.9801Z',
    main: 'M83.77 53.94V108.55C83.77 108.55 94.41 121.39 85.49 122.38C76.57 123.38 78.94 119.1 78.94 119.1C78.94 119.1 74.93 125.29 71.39 121.92C67.84 118.55 74.6 110.56 74.6 110.56L76.68 54.03C76.68 54.03 69.4 43.56 74.86 44.02C80.32 44.48 79.5 49.3 79.5 49.3C79.5 49.3 83.23 40.47 86.96 44.39C90.69 48.3 83.77 53.95 83.77 53.95V53.94Z'
  },
  {
    shadow:
      'M106.88 113.06L104.21 58.5201C104.21 58.5201 92.95 46.2201 101.81 44.7901C110.67 43.3501 108.52 47.7401 108.52 47.7401C108.52 47.7401 112.22 41.3601 115.93 44.5501C119.64 47.7401 113.28 56.0601 113.28 56.0601L113.98 112.63C113.98 112.63 121.76 122.73 116.29 122.54C110.81 122.35 111.4 117.49 111.4 117.49C111.4 117.49 108.1 126.49 104.19 122.76C100.27 119.03 106.9 113.06 106.9 113.06H106.88Z',
    main: 'M107.75 112.06L105.08 57.5201C105.08 57.5201 93.82 45.2201 102.68 43.7901C111.54 42.3501 109.39 46.7401 109.39 46.7401C109.39 46.7401 113.09 40.3601 116.8 43.5501C120.51 46.7401 114.15 55.0601 114.15 55.0601L114.85 111.63C114.85 111.63 122.63 121.73 117.16 121.54C111.69 121.35 112.27 116.49 112.27 116.49C112.27 116.49 108.97 125.49 105.06 121.76C101.14 118.03 107.77 112.06 107.77 112.06H107.75Z'
  }
];

// ─── Value 3: three vertical bones ────────────────────────────────────

const FACE_3: DiceFaceIndicator[] = [
  {
    shadow:
      'M69.3801 52.9801V107.59C69.3801 107.59 80.0201 120.43 71.1001 121.42C62.1801 122.42 64.5501 118.14 64.5501 118.14C64.5501 118.14 60.5401 124.33 57.0001 120.96C53.4501 117.59 60.2101 109.6 60.2101 109.6L62.2901 53.0701C62.2901 53.0701 55.0101 42.6001 60.4701 43.0601C65.9301 43.5201 65.1101 48.3401 65.1101 48.3401C65.1101 48.3401 68.8401 39.5101 72.5701 43.4301C76.3001 47.3501 69.3801 52.9901 69.3801 52.9901V52.9801Z',
    main: 'M68.45 53.94V108.55C68.45 108.55 79.09 121.39 70.17 122.38C61.25 123.38 63.62 119.1 63.62 119.1C63.62 119.1 59.61 125.29 56.07 121.92C52.53 118.55 59.28 110.56 59.28 110.56L61.36 54.03C61.36 54.03 54.08 43.56 59.54 44.02C65 44.48 64.18 49.3 64.18 49.3C64.18 49.3 67.91 40.47 71.64 44.39C75.37 48.3 68.45 53.95 68.45 53.95V53.94Z'
  },
  {
    shadow:
      'M126.12 52.9801V107.59C126.12 107.59 136.76 120.43 127.84 121.42C118.92 122.42 121.29 118.14 121.29 118.14C121.29 118.14 117.28 124.33 113.74 120.96C110.19 117.59 116.95 109.6 116.95 109.6L119.03 53.0701C119.03 53.0701 111.75 42.6001 117.21 43.0601C122.67 43.5201 121.85 48.3401 121.85 48.3401C121.85 48.3401 125.58 39.5101 129.31 43.4301C133.04 47.3401 126.12 52.9901 126.12 52.9901V52.9801Z',
    main: 'M125.2 53.94V108.55C125.2 108.55 135.84 121.39 126.92 122.38C118 123.38 120.37 119.1 120.37 119.1C120.37 119.1 116.36 125.29 112.82 121.92C109.28 118.55 116.03 110.56 116.03 110.56L118.11 54.03C118.11 54.03 110.83 43.56 116.29 44.02C121.75 44.48 120.93 49.3 120.93 49.3C120.93 49.3 124.66 40.47 128.39 44.39C132.12 48.3 125.2 53.95 125.2 53.95V53.94Z'
  },
  {
    shadow:
      'M91.56 113.06L88.89 58.5201C88.89 58.5201 77.63 46.2201 86.49 44.7901C95.35 43.3601 93.2 47.7401 93.2 47.7401C93.2 47.7401 96.9 41.3601 100.61 44.5501C104.32 47.7401 97.96 56.0601 97.96 56.0601L98.66 112.63C98.66 112.63 106.44 122.73 100.97 122.54C95.49 122.35 96.08 117.49 96.08 117.49C96.08 117.49 92.78 126.49 88.87 122.76C84.95 119.03 91.58 113.06 91.58 113.06H91.56Z',
    main: 'M92.4399 112.06L89.7699 57.5201C89.7699 57.5201 78.5099 45.2201 87.3699 43.7901C96.2299 42.3501 94.0799 46.7401 94.0799 46.7401C94.0799 46.7401 97.7799 40.3601 101.49 43.5501C105.2 46.7401 98.8399 55.0601 98.8399 55.0601L99.5399 111.63C99.5399 111.63 107.32 121.73 101.85 121.54C96.3699 121.35 96.9599 116.49 96.9599 116.49C96.9599 116.49 93.6599 125.49 89.7499 121.76C85.8299 118.03 92.4599 112.06 92.4599 112.06H92.4399Z'
  }
];

// ─── Value 4: left vertical bone + two angled bones (right) ──────────

const FACE_4: DiceFaceIndicator[] = [
  {
    shadow:
      'M69.3801 52.9801V107.59C69.3801 107.59 80.0201 120.43 71.1001 121.42C62.1801 122.42 64.5501 118.14 64.5501 118.14C64.5501 118.14 60.5401 124.33 57.0001 120.96C53.4501 117.59 60.2101 109.6 60.2101 109.6L62.2901 53.0701C62.2901 53.0701 55.0101 42.6001 60.4701 43.0601C65.9301 43.5201 65.1101 48.3401 65.1101 48.3401C65.1101 48.3401 68.8401 39.5101 72.5701 43.4301C76.3001 47.3401 69.3801 52.9901 69.3801 52.9901V52.9801Z',
    main: 'M68.45 53.94V108.55C68.45 108.55 79.09 121.39 70.17 122.38C61.25 123.38 63.62 119.1 63.62 119.1C63.62 119.1 59.61 125.29 56.07 121.92C52.53 118.55 59.28 110.56 59.28 110.56L61.36 54.03C61.36 54.03 54.08 43.56 59.54 44.02C65 44.48 64.18 49.3 64.18 49.3C64.18 49.3 67.91 40.47 71.64 44.39C75.37 48.3 68.45 53.95 68.45 53.95V53.94Z'
  },
  {
    shadow:
      'M137.71 54.73L120.85 106.67C120.85 106.67 127.02 122.16 118.22 120.36C109.42 118.56 113 115.22 113 115.22C113 115.22 107.28 119.87 104.94 115.57C102.6 111.27 111.5 105.75 111.5 105.75L130.92 52.62C130.92 52.62 127.23 40.42 132.28 42.54C137.33 44.66 135.07 48.99 135.07 48.99C135.07 48.99 141.34 41.74 143.69 46.62C146.03 51.49 137.71 54.73 137.71 54.73Z',
    main: 'M136.54 55.36L119.68 107.3C119.68 107.3 125.85 122.79 117.05 120.99C108.26 119.19 111.83 115.85 111.83 115.85C111.83 115.85 106.11 120.5 103.77 116.2C101.43 111.9 110.33 106.38 110.33 106.38L129.75 53.25C129.75 53.25 126.06 41.05 131.11 43.17C136.16 45.29 133.9 49.62 133.9 49.62C133.9 49.62 140.17 42.37 142.52 47.25C144.87 52.13 136.54 55.36 136.54 55.36Z'
  },
  {
    shadow:
      'M85.75 54.33L102.99 106.14C102.99 106.14 96.94 121.68 105.72 119.81C114.5 117.94 110.02 113 110.02 113C110.02 113 116.66 119.24 118.96 114.92C121.26 110.6 112.33 105.15 112.33 105.15L92.51 52.16C92.51 52.16 96.11 39.93 91.08 42.09C86.05 44.25 88.34 48.56 88.34 48.56C88.34 48.56 82.01 41.36 79.71 46.25C77.41 51.14 85.75 54.31 85.75 54.31V54.33Z',
    main: 'M86.93 55.06L104.17 106.87C104.17 106.87 98.12 122.41 106.9 120.54C115.68 118.67 112.08 115.36 112.08 115.36C112.08 115.36 117.83 119.97 120.14 115.65C122.44 111.33 113.51 105.88 113.51 105.88L93.69 52.89C93.69 52.89 97.29 40.66 92.26 42.82C87.22 44.98 89.52 49.29 89.52 49.29C89.52 49.29 83.19 42.09 80.89 46.98C78.59 51.87 86.93 55.04 86.93 55.04V55.06Z'
  }
];

// ─── Value 5: two angled bones (value 4 right-side pair) ─────────────

const FACE_5: DiceFaceIndicator[] = [
  {
    shadow:
      'M119.07 54.73L102.21 106.67C102.21 106.67 108.38 122.16 99.58 120.36C90.79 118.56 94.36 115.22 94.36 115.22C94.36 115.22 88.64 119.87 86.3 115.57C83.96 111.27 92.86 105.75 92.86 105.75L112.28 52.62C112.28 52.62 108.59 40.42 113.64 42.54C118.69 44.66 116.43 48.99 116.43 48.99C116.43 48.99 122.7 41.74 125.05 46.62C127.39 51.49 119.07 54.73 119.07 54.73Z',
    main: 'M117.89 55.36L101.03 107.3C101.03 107.3 107.2 122.79 98.4 120.99C89.61 119.19 93.18 115.85 93.18 115.85C93.18 115.85 87.46 120.5 85.12 116.2C82.78 111.9 91.68 106.38 91.68 106.38L111.1 53.25C111.1 53.25 107.41 41.05 112.46 43.17C117.51 45.29 115.25 49.62 115.25 49.62C115.25 49.62 121.52 42.37 123.87 47.25C126.22 52.13 117.89 55.36 117.89 55.36Z'
  },
  {
    shadow:
      'M67.1001 54.33L84.3401 106.14C84.3401 106.14 78.2901 121.68 87.0701 119.81C95.8501 117.94 91.3701 113 91.3701 113C91.3701 113 98.0101 119.24 100.31 114.92C102.61 110.6 93.6801 105.15 93.6801 105.15L73.8601 52.16C73.8601 52.16 77.4601 39.93 72.4301 42.09C67.4001 44.25 69.6901 48.56 69.6901 48.56C69.6901 48.56 63.3601 41.36 61.0601 46.25C58.7601 51.14 67.1001 54.31 67.1001 54.31V54.33Z',
    main: 'M68.28 55.06L85.52 106.87C85.52 106.87 79.4701 122.41 88.25 120.54C97.03 118.67 93.43 115.36 93.43 115.36C93.43 115.36 99.18 119.97 101.49 115.65C103.79 111.33 94.8601 105.88 94.8601 105.88L75.0401 52.89C75.0401 52.89 78.6401 40.66 73.6101 42.82C68.5701 44.98 70.8701 49.29 70.8701 49.29C70.8701 49.29 64.5401 42.09 62.2401 46.98C59.9401 51.87 68.28 55.04 68.28 55.04V55.06Z'
  }
];

// ─── Value 6: skull / head-bone (complex multi-path shape) ────────────
// The value 6 face uses a skull/head-bone design with gradient-filled elements.
// The indicator is the large skull shape occupying the center of the face.
// Note: this value uses special rendering in the component (not simple shadow+white pairs).

const FACE_6: DiceFaceIndicator[] = [
  {
    // Dark drop-shadow fragment (top center of skull cap)
    shadow: 'M98.57 121.27C98.31 121.35 98.05 121.41 97.77 121.43L98.57 121.27Z',
    main: 'M98.57 121.27C98.31 121.35 98.05 121.41 97.77 121.43L98.57 121.27Z'
  },
  {
    // Skull jaw / lower portion (dark)
    shadow:
      'M101.44 113.36L100.9 118.39C100.75 119.76 99.8001 120.86 98.5601 121.27L97.7601 121.43L94.0401 122.17L94.1401 121.1L92.5301 120.93C90.6601 120.73 89.7101 118.74 89.7101 116.86C89.7101 115.65 88.9901 112.35 88.9901 112.35L101.43 113.37L101.44 113.36Z',
    main: 'M93.18 122.19L88.58 121.7C86.71 121.5 85.76 119.51 85.76 117.63C85.76 116.42 85.03 113.12 85.03 113.12L97.47 114.14L96.93 119.17C96.73 121.04 95.04 122.4 93.17 122.2L93.18 122.19Z'
  },
  {
    // Skull left eye socket / lower-left jaw (dark)
    shadow:
      'M88.7501 110.85L88.2101 115.88C88.0401 117.48 86.7701 118.71 85.2301 118.9H85.2401L81.3701 119.66L81.1001 118.5C79.5601 118.02 78.7201 116.2 78.5101 114.51C78.2901 112.7 76.0701 108.01 76.0701 108.01L88.7501 110.85Z',
    main: 'M80.48 119.68L77.64 119.38C75.77 119.18 74.77 117.15 74.54 115.28C74.32 113.47 72.1 108.78 72.1 108.78L84.78 111.62L84.24 116.65C84.04 118.52 82.35 119.88 80.48 119.68Z'
  },
  {
    // Nose bridge connector (dark)
    shadow:
      'M85.8 100.42C85.32 100.33 84.79 100.49 84.44 100.93L82.96 102.8L81.42 104.62C80.99 105.13 80.99 105.79 81.3 106.28C80.23 106.09 79.76 104.81 80.47 103.98L82.01 102.16L83.49 100.3C83.82 99.88 84.31 99.72 84.78 99.78C85.19 99.83 85.57 100.06 85.8 100.43V100.42Z',
    main: 'M84.65 107.28L82.3 106.93C81.2 106.77 80.71 105.46 81.43 104.62L82.97 102.8L84.45 100.93C85.14 100.06 86.52 100.29 86.89 101.33L87.7 103.57L88.58 105.78C88.99 106.81 88.11 107.89 87.01 107.69L84.67 107.27L84.65 107.28Z'
  }
];

// Export the skull face data separately for special gradient rendering
export const FACE_6_SKULL_BODY =
  'M138.91 84.22L138.64 86.04C135.64 106.11 116.77 120.08 96.69 117.09L79.14 114.47C61 111.76 48.38 94.7 51.09 76.57L51.89 71.24C55.42 47.57 77.68 31.1 101.35 34.63L102.3 34.77C125.97 38.3 142.44 60.56 138.91 84.23V84.22Z';

export const FACE_6_RIGHT_EYE_SHADOW =
  'M108.86 100.45C108.53 100.54 108.2 100.62 107.86 100.69C100.13 102.19 92.5 96.43 90.84 87.83C89.18 79.23 94.09 71.04 101.82 69.54C102.16 69.47 102.5 69.42 102.83 69.39C95.68 71.37 91.25 79.22 92.84 87.44C94.44 95.66 101.48 101.29 108.85 100.45H108.86Z';

export const FACE_6_RIGHT_EYE_MAIN =
  'M108.874 100.465C116.123 99.0583 120.649 90.9606 118.984 82.3779C117.319 73.7953 110.092 67.9778 102.844 69.3843C95.5946 70.7907 91.0682 78.8884 92.7333 87.471C94.3985 96.0536 101.625 101.871 108.874 100.465Z';

export const FACE_6_LEFT_EYE_SHADOW =
  'M74.63 96.35C74.74 96.35 74.86 96.35 74.97 96.35C73.95 96.76 72.88 96.95 71.79 96.89C66.08 96.59 61.92 89.59 62.49 81.25C63.05 73.08 67.95 66.66 73.51 66.69C69.2 68.41 65.79 73.96 65.33 80.7C64.76 89.04 68.92 96.04 74.63 96.34V96.35Z';

export const FACE_6_LEFT_EYE_MAIN =
  'M83.7669 82.287C84.2679 74.0013 80.2095 67.0144 74.7022 66.6814C69.195 66.3485 64.3243 72.7954 63.8233 81.0811C63.3224 89.3668 67.3808 96.3537 72.888 96.6867C78.3953 97.0196 83.266 90.5727 83.7669 82.287Z';

// ─── Face indicators lookup ────────────────────────────────────────────

export const CRYPT_DICE_FACES: Record<number, DiceFaceIndicator[]> = {
  1: FACE_1,
  2: FACE_2,
  3: FACE_3,
  4: FACE_4,
  5: FACE_5,
  6: FACE_6
};
